import { useState, useRef, useCallback, useEffect, useContext, createContext } from 'react';
import { useDispatch } from 'react-redux';
import * as appActions from 'Actions/app';

export interface TempoContextType {
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  tempo: number;
  setTempo: React.Dispatch<React.SetStateAction<number>>;
  performanceTempo: number;
  setPerformanceTempo: React.Dispatch<React.SetStateAction<number>>;
  sliderValue: number;
  setSliderValue: React.Dispatch<React.SetStateAction<number>>;
  tempPctAdjustment: number;
  setTempPctAdjustment: React.Dispatch<React.SetStateAction<number>>;
  resetTemp: () => void;
  manualTempoInput: string;
  setManualTempoInput: React.Dispatch<React.SetStateAction<string>>;
  setManualTempoChange: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  max_tempo_value: number;
  min_tempo_value: number;
  mapTempoToSlider: (tempo: number) => void;
  tempoContainerRef: React.RefObject<HTMLImageElement | null>;
  tempoInputRef: React.RefObject<HTMLInputElement | null>;
  tempoToggleRef: React.RefObject<HTMLDivElement | null>;
}

const TempoContext = createContext<TempoContextType | undefined>(undefined);

export const TempoProvider: React.FC<{
  goalTempo: number;
  children: React.ReactNode;
}> = ({ goalTempo, children }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [tempo, setTempo] = useState(72);
  const [performanceTempo, setPerformanceTempo] = useState(goalTempo);
  const [sliderValue, setSliderValue] = useState(50);
  const [tempPctAdjustment, setTempPctAdjustment] = useState(100);
  const [manualTempoInput, setManualTempoInput] = useState(goalTempo.toString());
  const tempoContainerRef = useRef<HTMLImageElement | null>(null);
  const tempoInputRef = useRef<HTMLInputElement | null>(null);
  const tempoToggleRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useDispatch();

  const resetTemp = useCallback(() => {
    setSliderValue(50);
    setTempPctAdjustment(100);
    setTempo(goalTempo);
    setPerformanceTempo(goalTempo);
  }, [goalTempo]);

  const MIN_TEMPO_PCT = 0.1;
  const MAX_TEMPO_PCT = 3.0;
  const max_tempo_value = performanceTempo != null ? performanceTempo * MAX_TEMPO_PCT : 0;
  const min_tempo_value = performanceTempo != null ? performanceTempo * MIN_TEMPO_PCT : 9999;

  const mapTempoToSlider = (newTempo: number) => {
    if (performanceTempo != null) {
      const newTempPctAdjustment = (100 * newTempo) / performanceTempo; // DO not round yet otherwise will not trigger change in tempo (since based on percentage). Only round in display.
      let newValue = ((newTempPctAdjustment - 100) * (100 / 2)) / (2 * 100) + 100 / 2;
      if (newValue <= 50) {
        newValue = ((newTempPctAdjustment - 10) / 90) * (100 / 2);
      }
      setSliderValue(newValue);
      setTempPctAdjustment(newTempPctAdjustment);
    }
  };

  useEffect(() => {
    setManualTempoInput(tempo.toString());
  }, [tempo]);

  useEffect(() => {
    dispatch(appActions.setTempo({ tempo }));
  }, [tempo]);

  const setManualTempoChange = () => {
    let value = parseInt(manualTempoInput, 10);
    if (!isNaN(value)) {
      if (value > max_tempo_value) {
        value = max_tempo_value;
      } else if (value < min_tempo_value) {
        value = min_tempo_value;
      }
      mapTempoToSlider(value);
    }
    setManualTempoInput(tempo.toString());
  };

  useEffect(() => {
    setTempo(Math.round((performanceTempo * tempPctAdjustment) / 100.0));
  }, [performanceTempo, tempPctAdjustment]);

  return (
    <TempoContext.Provider
      value={{
        isExpanded,
        setIsExpanded,
        tempo,
        setTempo,
        performanceTempo,
        setPerformanceTempo,
        sliderValue,
        setSliderValue,
        tempPctAdjustment,
        setTempPctAdjustment,
        resetTemp,
        setManualTempoChange,
        manualTempoInput,
        setManualTempoInput,
        max_tempo_value,
        min_tempo_value,
        mapTempoToSlider,
        tempoContainerRef,
        tempoInputRef,
        tempoToggleRef,
      }}
    >
      {children}
    </TempoContext.Provider>
  );
};

export const useTempoContext = (): TempoContextType => {
  const context = useContext(TempoContext);
  if (!context) {
    throw new Error('useTempoContext must be used within a TempoProvider');
  }
  return context;
};
